import React, { ReactNode } from 'react';
import styles from './FilterSection.module.scss';
import Search from '@src/components/search/Search';
import Button from '@src/components/button/Button';
import Select from '@src/components/select/Select';
import { SelectOption } from '@src/types';
import FilterBoxes from './FilterBoxes';

type FilterSectionProps = {
  // Search props
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  
  // Filter selects
  filters: {
    value: string;
    onChange: (option: SelectOption) => void;
    label: string;
    options: SelectOption[];
    loading?: boolean;
    disabled?: boolean;
    showSearch?: boolean;
    doNotSort?: boolean;
  }[];
  
  // Action button
  actionButton?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    className?: string;
    color?: 'orange' | 'green' | 'red'| 'white' | 'greenbackground' | 'lightgreen' | 'whitebackground';
    size?: 'normal' | 'small' | 'extraSmall' | 'tiny';
  };
  
  // Reset button
  onReset?: () => void;
  resetButtonLabel?: string;
  hideResetButton?: boolean;
  
  // Advanced filters
  advancedFilters?: ReactNode;
  
  // Results count
  resultsCount?: number;
  resultsCountLabel?: string;
  
  // Additional content
  children?: ReactNode;
  
  // Control visibility of search input and divider
  hasSearchInput?: boolean;
  showDivider?: boolean;
};

/**
 * A reusable filter section component that can be used across different pages
 * to maintain UI consistency.
 */
const FilterSection: React.FC<FilterSectionProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filters,
  actionButton,
  onReset,
  resetButtonLabel = 'Reset',
  hideResetButton = false,
  advancedFilters,
  resultsCount,
  resultsCountLabel = 'Results',
  children,
  hasSearchInput = true,
  showDivider = true
}) => {
  // Create the filter content (without the action button)
  const filterContent = (
    <>
      {hasSearchInput && (
        <Search 
          onChange={onSearchChange} 
          value={searchValue} 
          placeholder={searchPlaceholder}
        />
      )}
      
      {filters.map((filter, index) => (
        <Select
          key={`filter-${index}`}
          inDropDown={true}
          value={filter.value}
          onChange={filter.onChange}
          label={filter.label}
          options={filter.options}
          loading={filter.loading}
          disabled={filter.disabled}
          showSearch={filter.showSearch !== undefined ? filter.showSearch : true}
          doNotSort={filter.doNotSort}
        />
      ))}
      
      {advancedFilters && (
        <div className={styles.advancedFilters}>
          {advancedFilters}
        </div>
      )}
      
      {children}
    </>
  );
  
  // Create the action button content
  const actionButtonContent = actionButton && (
    <div className={styles.actionButtonContainer}>
      <Button
        onClick={actionButton.onClick}
        className={`${styles.actionButton} ${actionButton.className || ''}`}
        label={actionButton.label}
        color={actionButton.color || "orange"}
        size={actionButton.size || "normal"}
        disabled={actionButton.disabled}
      />
    </div>
  );

  return (
    <FilterBoxes
      mainFilterContent={filterContent}
      actionButtonContent={actionButtonContent}
      onReset={onReset}
      resetButtonLabel={resetButtonLabel}
      resultsCount={resultsCount}
      resultsCountLabel={resultsCountLabel}
      hasAdvancedFilters={!!advancedFilters}
      hasSearchInput={hasSearchInput}
      hideResetButton={hideResetButton}
      showDivider={showDivider}
    />
  );
};

export default FilterSection;
